import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  DSDropdown,
  DSToggle,
} from "@components/design-system/molecules";
import {
  faSortAmountDown,
  faSortAmountUp,
  faUndo,
  faFilter,
  faSort,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";

import { PatientList } from "./PatientList";
import PageTitle from "../layout/PageTitle";

export const GET_PATIENT_ANALYTICS = gql`
  query patientAnalytics(
    $sortBy: String
    $filterBy: String
    $ascending: Boolean
  ) {
    patientAnalytics(
      sortBy: $sortBy
      filterBy: $filterBy
      ascending: $ascending
    ) {
      uuid
      createdAt
      name
      lastNameOfOwner
      breed
      birthday
      age
      sex
      species
    }
  }
`;

const sortOptions = [
  { label: "First name", value: "name" },
  { label: "Owner's Last Name", value: "lastNameOfOwner" },
  { label: "Created At", value: "createdAt" },
  { label: "Birthday", value: "birthday" },
];

const baseFilterOptions = [{ label: "None", value: "none" }];

const birthdayFilterOptions = [
  ...baseFilterOptions,
  { label: "Birthday last month", value: "birthdayLastMonth" },
  { label: "Birthday this month", value: "birthdayThisMonth" },
  { label: "Birthday next month", value: "birthdayNextMonth" },
];

export function PatientFilteringOptions({
  sortBy,
  setSortBy,
  filterBy,
  setFilterBy,
  ascending,
  setAscending,
  refetch,
}) {
  function resetFilters() {
    setSortBy("name");
    setFilterBy("none");
    setAscending(true);
    refetch({ sortBy: "name", filterBy: "none", ascending: true });
  }

  const filterOptions =
    sortBy === "birthday" ? birthdayFilterOptions : baseFilterOptions;

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0 md:space-x-4 mb-6 md:mb-2">
      <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
        <div className="flex flex-row items-center space-x-2">
          <h4 className="text-xs md:text-sm">
            <FontAwesomeIcon icon={faSort} /> Sort:
          </h4>
          <DSDropdown
            options={sortOptions}
            value={sortBy}
            onChange={(value) => setSortBy(value)}
            dataTestId="sort-dropdown"
            width="w-40"
            size="sm"
          />
        </div>
        <div className="flex flex-row items-center space-x-2">
          <h4 className="text-xs md:text-sm">
            <FontAwesomeIcon icon={faFilter} size="sm" /> Filter:
          </h4>
          <DSDropdown
            options={filterOptions}
            value={filterBy}
            onChange={(value) => setFilterBy(value)}
            dataTestId="filter-dropdown"
            width="w-48"
            size="sm"
          />
          {filterBy !== "none" ? (
            <button onClick={() => resetFilters()}>
              <h5 className="text-xs md:text-sm text-secondary hover:text-danger">
                <FontAwesomeIcon icon={faUndo} size="sm" /> Reset
              </h5>
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-row items-center space-x-2">
        <h4>
          <FontAwesomeIcon icon={faSortAmountUp} size="1x" />
        </h4>
        <DSToggle
          checked={ascending}
          onChange={() => {
            setAscending(!ascending);
            refetch();
          }}
          dataTestId="ascending-toggle"
        />
        <h4>
          <FontAwesomeIcon icon={faSortAmountDown} size="1x" />
        </h4>
      </div>
    </div>
  );
}

export default function PatientAnalytics() {
  const [sortBy, setSortBy] = useState("name");
  const [filterBy, setFilterBy] = useState("none");
  const [ascending, setAscending] = useState(true);

  const { refetch, data } = useQuery(GET_PATIENT_ANALYTICS, {
    variables: {
      sortBy,
      filterBy,
      ascending,
    },
  });

  return (
    <div>
      <PageTitle title="Patient Analytics" />

      <PatientFilteringOptions
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterBy={filterBy}
        setFilterBy={setFilterBy}
        ascending={ascending}
        setAscending={setAscending}
        refetch={refetch}
      />

      <PatientList patients={data?.patientAnalytics || []} />
    </div>
  );
}
